/**
 * Supabase Client — initialises and exports the Supabase JS client.
 *
 * Used by filesApi.js and storageApi.js for all database and file
 * storage operations. Replaces SharePoint Lists + OneDrive.
 *
 * Requires two environment variables:
 *   REACT_APP_SUPABASE_URL  — your Supabase project URL
 *   REACT_APP_SUPABASE_ANON_KEY — the public anon/service key
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Fail fast at module load if configuration is missing. A client built with
// empty URL/key "works" but every request fails later with confusing
// downstream errors (failed fetches, silent empty data); one clear error at
// the source is far easier to diagnose. The message names the missing
// variable(s) but never echoes any value (the anon key must never be logged).
const missingEnvVars = [];
if (!SUPABASE_URL) missingEnvVars.push('REACT_APP_SUPABASE_URL');
if (!SUPABASE_ANON_KEY) missingEnvVars.push('REACT_APP_SUPABASE_ANON_KEY');

if (missingEnvVars.length > 0) {
  throw new Error(
    `[Inbox] Supabase is not configured — missing required environment variable(s): ` +
      `${missingEnvVars.join(', ')}. Set them locally in your .env file, and for deployments in ` +
      `the Cloudflare Pages project under Settings → Environment variables. Note: Create React App ` +
      `bakes REACT_APP_* variables in at BUILD time, so after adding or changing them you must ` +
      `trigger a fresh deploy/rebuild — updating the Cloudflare dashboard values alone has no effect ` +
      `until the next build.`
  );
}

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export default supabase;
